import React, { useEffect, useState } from "react";
import {
  useAddProductMutation,
  useGetCategoryQuery,
  useGetSubCategoryQuery,
  useGetProductByIdQuery,
  useEditProductMutation,
  useCheckSkuUniqueQuery,
} from "../api/api";
import useDebounce, {
  validateField,
  getFieldType,
  tokenDecoder,
  IsServiceTypeVendor,
} from "../lib/utils";
import { useToast } from "../@/components/ui/use-toast";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import Loader from "./LoaderComponent";
import { setAppState } from "../redux/slices/appState";
import { useDispatch, useSelector } from "react-redux";
import CharacteristicsForm from "./CharacteristicsForm";
import TagSelector from "./TagSelector";
import {
  TAGS,
  STATUS,
  MAX_PRODUCT_TAGS_LIMIT,
  GOOGLE_CALENDAR_AUTH_URL,
  FILE_TYPE_ID,
  VIDEO_STATUS,
} from "../lib/constants";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../@/components/ui/select";
import { Weight } from "lucide-react";
import InfoTooltip from "./common/InfoTooltip";
import GenericDropdown from "./GenericDropDown";
import { useForm } from "react-hook-form";
import AvailabilityManager from "./Availability/AvailabilityManager";
import { LOOKUPS } from "../lib/constants";
import ModeDropdown from "./ModeDropdown";
import SortupDialog from "./common/SortupDialog";
import MediaUpload from "./MediaUpload";

interface ProductPageProps {
  isEditingProduct: boolean;
  isAddingProduct?: boolean;
}

interface Characteristic {
  id?: number;
  productId?: number;
  name: string;
  value: string;
}

interface RemovedCharacteristic {
  id: number;
}

interface Tag {
  id: number;
  name: string;
  tagId?: number;
}

interface RemovedTags {
  id: number;
}

interface VideoDetails {
  filePath: string;
  isVideoReplaced: boolean;
  videoId?: number;
  statusId?: number;
}

interface LayoutContext {
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
  showAlertOnRouteChange: boolean;
}

const defaultFormState = {
  sku: new Date().toISOString() + "-" + (Date.now() % 1000),
  quantityAvailable: "10",
  weightUnit: "KG",
  weight: "1",
  expectedShipHours: 0.5,
};

const ProductFrom: React.FC<ProductPageProps> = ({
  isAddingProduct,
  isEditingProduct,
}) => {
  const { setShowAlertOnRouteChange, showAlertOnRouteChange } =
    useOutletContext<LayoutContext>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [lookupId, setLookupId] = useState<number | null>(null);

  const [addProduct] = useAddProductMutation();
  const [editProduct] = useEditProductMutation();
  const { data: categoriesData, isSuccess: categoriesSuccess } =
    useGetCategoryQuery();
  const {
    data: productData,
    isSuccess: productSuccess,
    isLoading: productLoading,
    refetch: refetchProductById,
  } = useGetProductByIdQuery(
    { productId: parseInt(id as string) },
    { skip: !isEditingProduct }
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(-1);
  const {
    data: subCategoriesData,
    isSuccess: subCategoriesSuccess,
    isLoading: subCategoriesLoading,
    refetch: refetchSubCategories,
  } = useGetSubCategoryQuery({
    parentCategoryId: selectedCategoryId,
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [linkAccountDialog, setLinkAccountDialog] = useState(false);
  const [addProductLoader, setAddProductLoader] = useState(false);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | undefined
  >(1);
  const [productImages, setProductImages] = useState<any>([]);
  const [videoDetails, setVideoDetails] = useState<VideoDetails>({
    filePath: "",
    isVideoReplaced: false,
    videoId: undefined,
    statusId: undefined,
  });
  const [characteristics, setCharacteristics] = useState<Characteristic[]>([
    { name: "Details", value: "" },
  ]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [timeSlots, setTimeSlots] = useState<any>([]);
  const [removedCharacteristics, setRemovedCharacteristics] = useState<
    RemovedCharacteristic[]
  >([]);
  const [removedTags, setRemovedTags] = useState<RemovedTags[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [formState, setFormState] = useState<any>({
    productName: null,
    description: null,
    sku: undefined,
    price: undefined,
    oldPrice: undefined,
    categoryId: undefined,
    subCategoryId: undefined,
    statusId: 12,
    discountValue: undefined,
    quantityAvailable: undefined,
    defaultImageUrl: undefined,
    discountType: undefined,
    weightUnit: undefined,
    weight: null,
    removedCharacteristics: undefined,
    expectedShipHours: 0.5,
    characteristics: [],
    tags: [],
    modeId: "",
  });
  const [formError, setFormError] = useState<any>({
    productNameError: null,
    descriptionError: null,
    priceError: null,
    categoryIdError: null,
    subCategoryIdError: null,
    quantityAvailableError: null,
    skuError: null,
    modeIdError: null,
  });
  const debouncedSKUSearchQuery = useDebounce(formState.sku, 500);
  const {
    data: checkSkuUniqueQueryData,
    isSuccess: checkSkuUniqueQuerySuccess,
    ...checkSkuUniqueQuery
  } = useCheckSkuUniqueQuery(
    {
      sku:
        debouncedSKUSearchQuery === productData?.data?.sku
          ? ""
          : debouncedSKUSearchQuery,
    },
    { skip: !debouncedSKUSearchQuery }
  );

  const dispatch = useDispatch();
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const isDistanceRangApplicable = decodedToken.isDistanceRangApplicable;
  const serviceTypeId = decodedToken.serviceTypeId;

  const {
    register,
    // handleSubmit,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<any>();

  const isServiceTypeVendor = IsServiceTypeVendor();

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (type === "change") {
        setShowAlertOnRouteChange(true);
      } else if (type === "blur") {
        setShowAlertOnRouteChange(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (debouncedSKUSearchQuery) {
      checkSkuUniqueQuery.refetch();
      if (checkSkuUniqueQueryData?.data.isUnique === false) {
        setFormError((prevState: any) => ({
          ...prevState,
          skuError: "SKU already exists",
        }));
      } else {
        setFormError((prevState: any) => ({
          ...prevState,
          skuError: null,
        }));
      }
    }
  }, [debouncedSKUSearchQuery, checkSkuUniqueQueryData]);

  useEffect(() => {
    if (productSuccess && productData) {
      const mediaData = [
        ...(productData?.data?.productImages?.map((image) => {
          return {
            ...image,
            fileType: FILE_TYPE_ID.IMAGE,
            statusId: VIDEO_STATUS.VIDEO_PUBLISHED,
          };
        }) ?? []),
        ...(productData?.data?.productVideos?.map((video) => {
          return {
            ...video,
            fileType: FILE_TYPE_ID.VIDEO,
            statusId: video.fileStatusId,
          };
        }) ?? []),
      ].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

      setProductImages(mediaData);

      setCharacteristics(
        productData?.data?.characteristics || [{ name: "Details", value: "" }]
      );
      setSelectedCategoryId(productData?.data?.categoryId as number);
      setSelectedSubCategoryId(productData?.data?.subCategoryId as number);
      setFormState({
        productName: productData?.data?.productName,
        description: productData?.data?.description,
        sku: productData?.data?.sku || "",
        price:
          productData?.data?.oldPrice !== null
            ? productData?.data?.oldPrice
            : productData?.data?.price,
        oldPrice: productData?.data?.oldPrice,
        categoryId: productData?.data?.categoryId,
        subCategoryId: productData?.data?.subCategoryId,
        statusId: productData?.data?.statusId,
        discountType: productData?.data?.discountType || undefined,
        discountValue: productData?.data?.discountValue || undefined,
        quantityAvailable: productData?.data?.quantityAvailable,
        productId: productData?.data?.productId,
        productImages: productData?.data?.productImages || undefined,
        weightUnit: productData?.data?.weightUnit || undefined,
        weight: productData?.data?.weight || null,
        expectedShipHours: productData?.data?.expectedShipHours || 0.5,
        tags: productData?.data?.tags || [],
        modeId: productData?.data?.modeId || undefined,
      });
      setTags(productData?.data?.tags || []);
      setTimeSlots(productData?.data?.timeSlots || []);
      if (productData?.data?.productVideos) {
        setVideoDetails({
          filePath: productData?.data?.productVideos[0]?.filePath || "",
          videoId: productData?.data?.productVideos[0]?.videoId || undefined,
          isVideoReplaced: false,
          statusId:
            productData?.data?.productVideos[0]?.fileStatusId || undefined,
        });
      }
    }
    setShowAlertOnRouteChange(false);
  }, [productSuccess, productData]);

  useEffect(() => {
    setSelectedSubCategoryId(productData?.data?.subCategoryId);
    // setShowAlertOnRouteChange(false);
  }, [subCategoriesData, selectedSubCategoryId]);

  // useEffect(() => {
  //   if (categoriesSuccess && categoriesData) {
  //     setSelectedSubCategoryId(selectedCategoryId);
  //   }
  // }, [selectedSubCategoryId]);

  useEffect(() => {
    if (subCategoriesSuccess && subCategoriesData) {
      setFormState((prevState: any) => ({
        ...prevState,
        subCategoryId:
          selectedSubCategoryId == -1 ? undefined : selectedSubCategoryId,
      }));
    }
  }, [subCategoriesSuccess, subCategoriesData, selectedSubCategoryId]);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target;

    if (name === "price") {
      setFormState((prevState: any) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      if (formState[name] !== value) {
        setShowAlertOnRouteChange(true);
      }

      let updatedValue: string | number = value;

      if (name === "quantityAvailable") {
        updatedValue = value.replace(/[^0-9]/g, "");
      }

      if (name === "categoryId") {
        const parentCategoryId: any = parseInt(value);
        setSelectedCategoryId(
          parentCategoryId == -1 ? undefined : parentCategoryId
        );
        setSelectedSubCategoryId(-1);
        setFormState((prevState: any) => ({
          ...prevState,
          categoryId: parentCategoryId == -1 ? undefined : parentCategoryId,
          subCategoryId: undefined,
        }));
        refetchSubCategories();
      } else if (name === "subCategoryId") {
        updatedValue = parseInt(value, 10);
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: updatedValue == -1 ? undefined : updatedValue,
        }));
      } else if (name === "discountType") {
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: updatedValue == -1 ? null : updatedValue,
          discountValue: null,
        }));
      } else if (name === "weightUnit") {
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: updatedValue === "" ? null : updatedValue,
          weight: null,
        }));
      } else {
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: updatedValue === "" ? undefined : updatedValue,
        }));
      }

      const error = validateField(getFieldType(name), updatedValue, {
        required: true,
      });
      setFormError((prevState: any) => ({
        ...prevState,
        [`${name}Error`]: error,
      }));
    }
  };

  const handleFormatPayload = (
    timeSlotsPayload: any,
    adjustedBlockedDates: any,
    removedTimeSlots: any,
    unavailableSlotsIds: any
  ) => {
    const payload = {
      removedTimeSlotIds:
        removedTimeSlots.length > 0 || unavailableSlotsIds.length > 0
          ? [...removedTimeSlots, ...unavailableSlotsIds]
          : undefined,
      timeSlots: timeSlotsPayload.length > 0 ? timeSlotsPayload : undefined,
      blockDate:
        adjustedBlockedDates.length > 0 ? adjustedBlockedDates : undefined,
    };
    setFormState((prevState: any) => ({
      ...prevState,
      ...payload,
    }));
  };

  const handleSubmit = async (status: number, e: React.FormEvent) => {
    e.preventDefault();
    setAddProductLoader(status === STATUS.ProductActive ? true : false);
    setIsDisabled(true);
    let formStateToSubmit = formState;
    setShowAlertOnRouteChange(false);
    if (checkSkuUniqueQueryData?.data.isUnique === false) {
      setIsDisabled(false);
      return;
    }

    if (isServiceTypeVendor) {
      formStateToSubmit = {
        ...formStateToSubmit,
        ...defaultFormState,
      };
    }

    const errors: { [key: string]: string | null } = {
      productNameError: validateField("text", formStateToSubmit.productName, {
        required: true,
      }),
      descriptionError: validateField("text", formStateToSubmit.description, {
        required: true,
      }),
      priceError: isServiceTypeVendor
        ? null
        : validateField("number", formStateToSubmit.price, { required: true }),
      quantityAvailableError: validateField(
        "quantity",
        formStateToSubmit.quantityAvailable,
        { required: true }
      ),
      categoryIdError: validateField("text", formStateToSubmit.categoryId, {
        required: true,
      }),
      subCategoryIdError: validateField(
        "text",
        formStateToSubmit.subCategoryId,
        {
          required: true,
        }
      ),
      expectedShipHours: validateField(
        "text",
        formStateToSubmit.expectedShipHours,
        {
          required: true,
        }
      ),
      weight: validateField("text", formStateToSubmit.weight, {
        required: true,
      }),
      weightUnit: validateField("text", formStateToSubmit.weightUnit, {
        required: true,
      }),
      skuError: validateField("text", formStateToSubmit.sku, {
        required: true,
      }),
    };
    if (isServiceTypeVendor) {
      errors.modeIdError = validateField("text", formStateToSubmit.modeId, {
        required: true,
      });
    }
    setFormError(errors);

    if (Object.values(errors).every((error) => error === null)) {
      const hasImages = productImages.some(
        (media: any) => media.fileType === FILE_TYPE_ID.IMAGE
      );
      if (!hasImages) {
        toast({
          title: "Alert!",
          description: `Please upload ${
            isServiceTypeVendor ? "service" : "product"
          } image(s)`,
        });
        setIsDisabled(false);
        setAddProductLoader(false);
        return;
      }

      try {
        delete formStateToSubmit?.oldPrice;
        formStateToSubmit.characteristics = characteristics;
        formStateToSubmit.tags = isAddingProduct
          ? selectedTags.map((tag) => ({ tagId: tag.id }))
          : [];
        const newTags = selectedTags.filter(
          (tag) => !tags?.some((fStateTag: any) => fStateTag.tagId === tag.id)
        );

        // setProductImages(formState.productImages);

        formStateToSubmit.productName = formState.productName;
        formStateToSubmit.description = formState.description;
        formStateToSubmit.price = parseFloat(formState.price);
        formStateToSubmit.expectedShipHours = parseFloat(
          formStateToSubmit.expectedShipHours
        );
        formStateToSubmit.quantityAvailable = parseInt(
          formStateToSubmit.quantityAvailable
        );
        const removedVideos = formStateToSubmit.productImages?.filter(
          (image: any) => image.fileType === FILE_TYPE_ID.VIDEO
        );

        formStateToSubmit.productImages =
          formStateToSubmit.productImages?.filter(
            (image: any) => image.fileType !== FILE_TYPE_ID.VIDEO
          );

        const removeVideo = formStateToSubmit.removedImages?.filter(
          (image: any) => image.id
        );

        formStateToSubmit.removedImages =
          formStateToSubmit.removedImages?.filter((image: any) => !image.id);

        const {
          categoryId,
          productId,
          // expectedShipHours,
          ...formWithoutCategory
        } = formStateToSubmit;
        const characteristicsWithId = characteristics.map(
          (characteristic, index) => ({
            ...characteristic,
            productId: parseInt(id as string),
          })
        );
        const formStateWithId = {
          ...formWithoutCategory,
          id: parseInt(id as string),
          statusId: status,
          subCategoryId: parseInt(formStateToSubmit.subCategoryId),
          price: parseFloat(formStateToSubmit.price),
          quantityAvailable: parseInt(formStateToSubmit.quantityAvailable),
          removedCharacteristics:
            removedCharacteristics.length > 0
              ? removedCharacteristics
              : undefined,
          characteristics: characteristicsWithId,
          discountType:
            formStateToSubmit.discountType === "0"
              ? "PERCENTAGE"
              : formStateToSubmit.discountType,
          discountValue:
            formStateToSubmit.discountType === "0"
              ? undefined
              : formStateToSubmit.discountValue,
          tags: newTags.map((tag) => ({
            tagId: tag.id,
            productId: parseInt(id as string),
          })),
          removedTags: removedTags.length > 0 ? removedTags : undefined,
          productImages: formStateToSubmit.productImages,
          modeId:
            formStateToSubmit.modeId === ""
              ? undefined
              : formStateToSubmit.modeId,
          deletedVideoFileObj:
            removeVideo?.length > 0 ? removeVideo[0] : undefined,
        };

        const videoData = {
          filePath: videoDetails.filePath,
          isVideoReplaced: isEditingProduct
            ? videoDetails.isVideoReplaced
            : undefined,
          id: isEditingProduct ? videoDetails?.videoId || undefined : undefined,
          order: removedVideos.length > 0 && removedVideos[0].order,
        };

        formWithoutCategory.statusId = status;
        formWithoutCategory.modeId =
          formStateToSubmit.modeId === ""
            ? undefined
            : formStateToSubmit.modeId;

        if (videoData.order === false) {
          delete formWithoutCategory.videoData;
          delete formStateWithId.videoData;
        } else {
          formWithoutCategory.videoData =
            videoData.filePath !== "" ? videoData : undefined;
          formStateWithId.videoData =
            videoData.filePath !== "" ? videoData : undefined;
        }

        const apiCall = isEditingProduct
          ? editProduct({ editProductDto: formStateWithId })
          : addProduct({ addProductDto: formWithoutCategory });
        const response: any = await apiCall;

        if (response.data?.message?.includes("success")) {
          toast({
            title: "Success!",
            description: `Your ${
              isServiceTypeVendor ? "service" : "product"
            } has been published successfully`,
          });

          if (isEditingProduct) {
            refetchProductById();
          }
          setIsDisabled(false);

          setShowAlertOnRouteChange(false);
          setRemovedTags([]);
          navigate(isServiceTypeVendor ? "/services" : "/products");
        } else {
          const isGoogleAccountLinked = response?.error?.data?.message
            ?.toLowerCase()
            .includes("not linked");

          if (isGoogleAccountLinked) {
            setLinkAccountDialog(true);
          } else {
            toast({
              title: "Error!",
              description: `${
                response?.error?.data?.message || "Something went wrong"
              }`,
            });
          }
        }
      } catch (error) {
        setIsDisabled(false);
        toast({
          title: "Error!",
          description: `${error}`,
        });
        console.error("Failed to add product", error);
      }
    } else {
      setIsDisabled(false);
      toast({
        title: "Error!",
        description: "Please fill out the mandatory fields!",
      });
    }
    setIsDisabled(false);
    setAddProductLoader(false);

    if (!formState.modeId && isServiceTypeVendor) {
      toast({
        title: "Error!",
        description: "Mode ID is required.",
      });
      return;
    }
    if (!formState.price) {
      toast({
        title: "Error!",
        description: "Price is required.",
      });
      return;
    }
  };

  type InputType = "quantity" | "priceOrDiscount" | "weight";

  const handleInput = (
    e: React.FormEvent<HTMLInputElement>,
    type: InputType
  ) => {
    const input = e.currentTarget;
    const currentValue = input.value;

    let isValidInput: boolean;
    let cleanedValue: string;

    switch (type) {
      case "quantity":
        isValidInput = /^[0-9]*$/.test(currentValue);
        cleanedValue = currentValue
          .replace(/[^0-9]/g, "")
          .replace(/^0+(?=\d)/, "");
        break;

      case "weight":
        isValidInput = /^\d*\.?\d{0,2}$/.test(currentValue);
        cleanedValue = currentValue
          .replace(/[^0-9.]/g, "")
          .replace(/(\..*?)\..*/g, "$1")
          .replace(/(\d*\.\d{2}).*/g, "$1");
        break;

      case "priceOrDiscount":
        isValidInput = /^\d*\.?\d{0,2}$/.test(currentValue);
        cleanedValue = currentValue
          .replace(/[^0-9.]/g, "")
          .replace(/(\..*?)\..*/g, "$1")
          .replace(/(\d*\.\d{2}).*/g, "$1");
        break;

      default:
        return;
    }

    if (!isValidInput) {
      input.value = cleanedValue;
    }
  };

  const handleLinkAccount = () => {
    setShowAlertOnRouteChange(false);
    setLinkAccountDialog(false);
    localStorage.setItem("googleCalenderRedirectPath", "services");
    window.open(GOOGLE_CALENDAR_AUTH_URL, "_self");
  };

  const tooltipContent = {
    productName: `A clear, descriptive name that helps customers understand what your ${
      IsServiceTypeVendor() ? "service" : "product"
    } is.`,
    sku: `A unique identifier for your ${
      IsServiceTypeVendor() ? "service" : "product"
    }. Used for inventory tracking and management.`,
    basePrice: `Enter the regular price of your ${
      IsServiceTypeVendor() ? "service" : "product"
    } in CAD (Canadian Dollar) before discounts or promotions.`,
    discountType: `Choose percentage for a % off or flat for a fixed amount discount on your ${
      IsServiceTypeVendor() ? "service" : "product"
    }.`,
    weightUnit: `Select the appropriate unit of measurement for your ${
      IsServiceTypeVendor() ? "service" : "product"
    }'s weight.`,
    shipmentTime: `Estimated time in hours before the ${
      IsServiceTypeVendor() ? "service" : "product"
    } is ready to be shipped after order placement.`,
  };

  // reload to show alert on route change
  // useEffect(() => {
  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     if (showAlertOnRouteChange) {
  //       event.preventDefault();
  //       event.returnValue = ""; // This is required for Chrome to show the alert
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //   };
  // }, [showAlertOnRouteChange]);

  return (
    <>
      <form className="bg-bgColor">
        <div className="flex gap-1 mt-1 p-6 space-x-2 bg-customOffWhiteColor">
          <button
            className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 rounded-lg"
            type="button"
            disabled={isDisabled || isVideoUploading}
            onClick={(e: any) => handleSubmit(STATUS.ProductActive, e)}
          >
            {!addProductLoader && (
              <img
                src="/Images/ProductFormImages/plane.png"
                className="w-5 h-5 mr-1"
                alt="Publish"
              />
            )}
            {addProductLoader ? "Publishing..." : "Publish"}
          </button>
          <button
            className="flex text-sm font-normal items-center justify-center bg-transparent text-customGrayText border border-customGrayText px-4 py-2 rounded-lg"
            disabled={isDisabled || isVideoUploading}
            type="button"
            onClick={(e: any) => handleSubmit(STATUS.ProductDraft, e)}
          >
            <img
              src="/Images/ProductFormImages/document-text.png"
              className="w-5 h-5 mr-1"
              alt="Draft"
            />
            Draft
          </button>
          <button
            className="flex text-sm font-normal items-center justify-center bg-transparent text-customGrayText border border-customGrayText px-4 py-2 rounded-lg"
            disabled={isDisabled || isVideoUploading}
            type="button"
            onClick={() => {
              setShowAlertOnRouteChange(false);
              navigate(isServiceTypeVendor ? "/services" : "/products");
            }}
          >
            <img
              src="/Images/ProductFormImages/close-square.png"
              className="w-5 h-5 mr-1"
              alt="Cancel"
            />
            Cancel
          </button>
        </div>
        <div className="grid md:grid-cols-4 p-6 pt-2 grid-cols-1 gap-4">
          <div className="md:col-span-2 w-auto space-y-4">
            {/* General Information */}
            <div className="bg-white p-4 shadow rounded">
              <h2 className="text-lg font-semibold mb-2">
                General Information
              </h2>
              <div>
                <label className="font-medium text-sm text-customGrayDark flex items-center">
                  Name<span className="text-red-500">*</span>
                  <InfoTooltip
                    content={`Provide a name for your ${
                      isServiceTypeVendor ? "service" : "product"
                    }.`}
                    className="ml-2"
                  />
                </label>
                <input
                  type="text"
                  name="productName"
                  value={formState.productName}
                  onChange={handleChange}
                  className={`mt-1 block w-full border border-gray-300 bg-grayBackgroundColor focus:outline-customGreenColor rounded-lg px-3 py-2 ${
                    formError.productNameError ? "border-red-500" : ""
                  }`}
                  placeholder="Type name here..."
                  required
                />
                {formError.productNameError && (
                  <p className="text-red-500 text-sm">
                    {formError.productNameError}
                  </p>
                )}
              </div>
              <div className="mt-4">
                <label className="flex font-medium text-sm text-customGrayDark items-center">
                  Description<span className="text-red-500 ">*</span>
                  <InfoTooltip
                    content={`Provide a detailed description of your ${
                      isServiceTypeVendor ? "service" : "product"
                    }.`}
                    className="ml-2"
                  />
                </label>
                <textarea
                  name="description"
                  maxLength={200}
                  value={formState.description}
                  onChange={handleChange}
                  className={`mt-1 resize-none min-h-40 block w-full border border-gray-300 focus:outline-customGreenColor rounded-lg bg-grayBackgroundColor px-3 py-2 ${
                    formError.descriptionError ? "border-red-500" : ""
                  }`}
                  placeholder="Type description here..."
                  required
                />
                {formError.descriptionError && (
                  <p className="text-red-500 text-sm">
                    {formError.descriptionError}
                  </p>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="bg-white p-4 shadow rounded">
              <h2 className="text-lg font-semibold mb-2">Category</h2>
              <div>
                <label className="flex items-center font-medium text-sm mb-1 text-customGrayDark">
                  Category<span className="text-red-500">*</span>
                  <InfoTooltip
                    content={`Select the category that best fits your ${
                      isServiceTypeVendor ? "service" : "product"
                    }.`}
                    className="ml-2"
                  />
                </label>
                <GenericDropdown
                  labelValue={
                    selectedCategoryId > 0
                      ? categoriesData?.data.find(
                          (cat) => cat.id === selectedCategoryId
                        )?.name || "Select a category"
                      : ""
                  }
                  onChange={(value) => {
                    setShowAlertOnRouteChange(true);
                    handleChange({ target: { name: "categoryId", value } });
                    const error = validateField("text", value, {
                      required: true,
                    });
                    setFormError((prevState: any) => ({
                      ...prevState,
                      categoryIdError: error,
                    }));
                  }}
                  options={
                    categoriesSuccess
                      ? categoriesData?.data?.map((category: any) => ({
                          value: category.id.toString(),
                          label: category.name,
                        }))
                      : []
                  }
                  placeholder="Select a category"
                  error={!!formError.categoryIdError}
                  dropdownMenuContentWidth="w-[38vw]"
                  className={`text-customGrayText h-11 bg-grayBackgroundColor justify-center items-center w-full ${
                    formError.categoryIdError
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  showAllOption={false}
                />
                {formError.categoryIdError && (
                  <p className="text-red-500 text-sm">
                    {formError.categoryIdError}
                  </p>
                )}
              </div>

              {/* Subcategory */}
              <div className="mt-4">
                <label className="flex items-center font-medium text-sm mb-1 text-customGrayDark">
                  Subcategory<span className="text-red-500">*</span>
                  <InfoTooltip
                    content={`Select the subcategory that best fits your ${
                      isServiceTypeVendor ? "service" : "product"
                    }.`}
                    className="ml-2"
                  />
                </label>
                <GenericDropdown
                  labelValue={
                    formState.subCategoryId && formState.subCategoryId > 0
                      ? subCategoriesData?.data.find(
                          (sub) => sub.id === formState.subCategoryId
                        )?.name || "Select a subcategory"
                      : ""
                  }
                  onChange={(value) => {
                    setShowAlertOnRouteChange(true);
                    handleChange({ target: { name: "subCategoryId", value } });
                    if (value === "-1") {
                      const error = validateField("text", value, {
                        required: true,
                      });
                      setFormError((prevState: any) => ({
                        ...prevState,
                        subCategoryIdError: error,
                      }));
                    } else {
                      setFormError((prevState: any) => ({
                        ...prevState,
                        subCategoryIdError: null,
                      }));
                    }
                  }}
                  options={
                    subCategoriesData?.data?.map((subCategory: any) => ({
                      value: subCategory.id.toString(),
                      label: subCategory.name,
                    })) || []
                  }
                  placeholder="Select a subcategory"
                  error={!!formError.subCategoryIdError}
                  dropdownMenuContentWidth="w-[38vw]"
                  className={`text-customGrayText h-11 bg-grayBackgroundColor justify-center items-center w-full ${
                    formError.subCategoryIdError
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  showAllOption={false}
                />
                {formError.subCategoryIdError && (
                  <p className="text-red-500 text-sm">
                    {formError.subCategoryIdError}
                  </p>
                )}
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white p-4 shadow rounded">
              <h2 className="text-lg font-semibold mb-2">Pricing</h2>
              <div>
                <label className="font-medium text-sm text-customGrayDark flex items-center">
                  Base Price<span className="text-red-500">*</span>
                  <InfoTooltip
                    content={`Enter the regular price of your ${
                      isServiceTypeVendor ? "service" : "product"
                    }.`}
                    className="ml-2"
                  />
                </label>
                <input
                  type="text"
                  name="price"
                  value={formState.price}
                  onInput={(e) => handleInput(e, "priceOrDiscount")}
                  onChange={handleChange}
                  className={`mt-1 block w-full border border-gray-300 rounded-lg bg-grayBackgroundColor focus:outline-customGreenColor px-3 py-2 ${
                    formError.priceError ? "border-red-500" : ""
                  }`}
                  placeholder="$ Type price here..."
                  required
                />
                {formError.priceError && (
                  <p className="text-red-500 text-sm">{formError.priceError}</p>
                )}
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Discount Type */}
                <div>
                  <label className="font-medium text-sm text-customGrayDark mb-1 flex items-center">
                    Discount Type<span className="text-red-500"></span>
                    <InfoTooltip
                      content="Choose between a percentage or flat discount."
                      className="ml-2"
                    />
                  </label>
                  <GenericDropdown
                    labelValue={
                      formState.discountType && formState.discountType !== "-1"
                        ? formState.discountType === "PERCENTAGE"
                          ? "Percentage"
                          : "Flat"
                        : ""
                    }
                    onChange={(value) => {
                      setShowAlertOnRouteChange(true);
                      handleChange({ target: { name: "discountType", value } });
                      if (value === "-1") {
                        const error = validateField("text", value, {
                          required: true,
                        });
                        setFormError((prevState: any) => ({
                          ...prevState,
                          discountTypeError: error,
                        }));
                      } else {
                        setFormError((prevState: any) => ({
                          ...prevState,
                          discountTypeError: null,
                        }));
                      }
                    }}
                    options={[
                      // { value: "-1", label: "Select a discount type" },
                      { value: "PERCENTAGE", label: "Percentage" },
                      { value: "FLAT", label: "Flat" },
                    ]}
                    placeholder="Select a discount type"
                    error={!!formError.discountTypeError}
                    className={`text-customGrayText bg-grayBackgroundColor justify-center items-center w-full ${
                      formError.discountTypeError
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    showAllOption={false}
                  />
                  {formError.discountTypeError && (
                    <p className="text-red-500 text-sm">
                      {formError.discountTypeError}
                    </p>
                  )}
                </div>

                {/* Discount Value */}
                <div>
                  <label className="flex items-center font-medium text-sm text-customGrayBase">
                    Discount Value
                    <InfoTooltip
                      content="Enter the discount amount or percentage value."
                      className="ml-2"
                    />
                  </label>
                  <input
                    type="text"
                    name="discountValue"
                    value={formState.discountValue}
                    onChange={handleChange}
                    onInput={(e) => handleInput(e, "priceOrDiscount")}
                    disabled={formState.discountType === "-1"}
                    className={`mt-1 block w-full border border-gray-300 rounded-lg bg-grayBackgroundColor ${
                      formState.discountType === "-1"
                        ? "cursor-not-allowed"
                        : ""
                    } focus:outline-customGreenColor px-3 py-2`}
                    placeholder="Type discount value here..."
                  />
                </div>
              </div>
            </div>

            {!isServiceTypeVendor && (
              <div className="bg-white p-4 shadow rounded">
                <h2 className="text-lg font-semibold mb-2">Inventory</h2>
                <div>
                  <label className="font-medium text-sm text-customGrayBase flex items-center">
                    SKU<span className="text-red-500">*</span>
                    <InfoTooltip content={tooltipContent.sku} />
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formState.sku}
                    onChange={handleChange}
                    className={`mt-1 block w-full border border-gray-300 rounded-lg bg-grayBackgroundColor focus:outline-customGreenColor px-3 py-2 ${
                      formError.skuError ? "border-red-500" : ""
                    }`}
                    placeholder="Enter SKU"
                    required
                  />
                  {formError.skuError && (
                    <p className="text-red-500 text-sm">{formError.skuError}</p>
                  )}
                </div>
                <div className="mt-4">
                  <label className="flex items-center font-medium text-sm text-customGrayBase">
                    Quantity Available<span className="text-red-500">*</span>
                    <InfoTooltip
                      content="Enter the quantity of this product available for sale."
                      className="ml-2"
                    />
                  </label>
                  <input
                    type="text"
                    name="quantityAvailable"
                    value={formState.quantityAvailable}
                    onChange={handleChange}
                    onInput={(e) => handleInput(e, "quantity")}
                    className={`mt-1 block w-full border border-gray-300 rounded-lg bg-grayBackgroundColor focus:outline-customGreenColor px-3 py-2 ${
                      formError.quantityAvailableError ? "border-red-500" : ""
                    }`}
                    placeholder="Type quantity available here..."
                    required
                  />
                  {formError.quantityAvailableError && (
                    <p className="text-red-500 text-sm">
                      {formError.quantityAvailableError}
                    </p>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-sm text-customGrayBase flex items-center">
                      Weight Unit<span className="text-red-500">*</span>
                      <InfoTooltip content={tooltipContent.weightUnit} />
                    </label>
                    <Select
                      name="weightUnit"
                      value={formState?.weightUnit ?? "-1"}
                      onValueChange={(value) => {
                        setShowAlertOnRouteChange(true);
                        handleChange({ target: { name: "weightUnit", value } });
                      }}
                    >
                      <SelectTrigger
                        className={`flex items-center text-customGrayText justify-between text-sm bg-grayBackgroundColor ${
                          formState.weightUnit > "0" && "text-black"
                        } mt-1 w-full border px-4 py-5 ${
                          formError.weightUnit
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:border-none focus:outline-customGreenColor rounded-lg`}
                      >
                        <SelectValue
                          className={`font-normal text-sm ${
                            formState.weightUnit > "-1"
                              ? "text-black"
                              : "text-customGrayText"
                          }`}
                          placeholder="Choose an option"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem disabled value="-1">
                            Select weight unit
                          </SelectItem>
                          <SelectItem value="KG">Kilogram (kg)</SelectItem>
                          <SelectItem value="G">Gram (g)</SelectItem>
                          <SelectItem value="MG">Milligram (mg)</SelectItem>
                          <SelectItem value="LB">Pound (lb)</SelectItem>
                          <SelectItem value="OZ">Ounce (oz)</SelectItem>
                          <SelectItem value="TON">Ton (t)</SelectItem>
                          <SelectItem value="STONE">Stone (st)</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="flex items-center font-medium text-sm text-customGrayBase">
                      Weight<span className="text-red-500">*</span>
                      <InfoTooltip
                        content="Enter the weight of the product."
                        className="ml-2"
                      />
                    </label>
                    <input
                      key={formState.weightUnit}
                      type="text"
                      name="weight"
                      value={formState.weight}
                      onChange={handleChange}
                      onInput={(e) => handleInput(e, "weight")}
                      className={`mt-1 block w-full border ${
                        formError.weight ? "border-red-500" : "border-gray-300"
                      } rounded-lg bg-grayBackgroundColor focus:outline-customGreenColor px-3 py-2`}
                      placeholder="Enter Weight"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Media Section */}
          <div className="md:col-span-2 w-full space-y-4">
            <MediaUpload
              productImages={productImages}
              productId={id as string}
              setProductImages={setProductImages}
              handleUpload={(payload: any) => {
                setShowAlertOnRouteChange(true);
                setFormState((prevState: any) => ({
                  ...prevState,
                  productImages: payload.imagesBlob,
                  removedImages:
                    payload.removeImages.length > 0
                      ? payload.removeImages
                      : undefined,
                }));
              }}
              setIsDisabled={setIsVideoUploading}
              setVideoDetails={setVideoDetails}
              isForEditVideo={isEditingProduct}
              filePath={videoDetails.filePath}
            />

            {/* Information */}
            <div className="bg-white p-4 shadow rounded relative ">
              <h2 className="text-lg font-semibold mb-2 flex items-center">
                Specifications
                <InfoTooltip
                  className="ml-2 color-customGrayText text-xs font-normal"
                  content={`Add ${
                    isServiceTypeVendor ? "service" : "product"
                  } specifications to help customers understand your ${
                    isServiceTypeVendor ? "service" : "product"
                  } better.`}
                />
              </h2>
              <CharacteristicsForm
                characteristics={characteristics}
                setCharacteristics={setCharacteristics}
                removedCharacteristics={removedCharacteristics}
                setRemovedCharacteristics={setRemovedCharacteristics}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
              {!isServiceTypeVendor && (
                <div className="mt-4">
                  <label className="font-medium text-sm text-customGrayBase flex items-center">
                    Ready for Shipment in (hrs)
                    <span className="text-red-500">*</span>
                    <InfoTooltip
                      content={tooltipContent.shipmentTime}
                      className="ml-2"
                    />
                  </label>
                  <input
                    type="number"
                    name="expectedShipHours"
                    value={formState.expectedShipHours}
                    onChange={handleChange}
                    className={`mt-1 block w-full border border-gray-300 rounded-lg bg-grayBackgroundColor focus:outline-customGreenColor px-3 py-2 ${
                      formError.expectedShipHours ? "border-red-500" : ""
                    }`}
                    placeholder="eg: 0.5"
                    required
                  />
                  {formError.expectedShipHours && (
                    <p className="text-red-500 text-sm">
                      {formError.expectedShipHours}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="bg-white p-4 shadow rounded relative">
              <h2 className="text-lg font-semibold mb-2 flex items-center">
                Tags
                <InfoTooltip
                  content={`Add tags to help customers find your ${
                    isServiceTypeVendor ? "service" : "product"
                  }.`}
                  className="ml-2 color-customGrayText text-xs font-normal"
                />
              </h2>
              <TagSelector
                fetchTagId={TAGS.ProductTags}
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                preloadedTags={formState.tags}
                removedTags={removedTags}
                setRemovedTags={setRemovedTags}
                maxTags={MAX_PRODUCT_TAGS_LIMIT}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
                serviceTypeId={serviceTypeId}
              />
            </div>
          </div>
        </div>
      </form>
      {IsServiceTypeVendor() && (
        <div className="p-6 -mt-5">
          <AvailabilityManager
            isForProduct={true}
            isEditingProduct={isEditingProduct}
            handleFormatPayload={handleFormatPayload}
            isShowSaveButton={false}
            productTimeSlots={timeSlots}
            isPreviewOnly={false}
            modeDropdown={
              <ModeDropdown
                selectedModeId={formState.modeId}
                onModeChange={(modeId) => {
                  setFormState((prevState: any) => ({
                    ...prevState,
                    modeId: modeId,
                  }));
                }}
                isDistanceRangApplicable={isDistanceRangApplicable}
                setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              />
            }
          />
        </div>
      )}

      {(isDisabled || productLoading || subCategoriesLoading) && (
        <Loader loading={isDisabled ? "Adding..." : "Loading..."} />
      )}

      {linkAccountDialog && (
        <SortupDialog
          isOpen={linkAccountDialog}
          onClose={() => setLinkAccountDialog(false)}
          bodyClassName="max-w-[200px]"
          header={"Account Link"}
          footerButtons={[
            {
              name: "Link",
              onClick: handleLinkAccount,
              className: "px-6 py-3 bg-customGreenColor text-white rounded-lg",
              disabled: false,
            },
            {
              name: "Close",
              onClick: () => {
                setLinkAccountDialog(false);
                navigate("/products");
              },
              className: "px-6 py-3 bg-white text-gray-900 border rounded-lg",
              disabled: false,
            },
          ]}
        >
          <p className="text-wrap text-black">
            Google Calendar is not linked to your account. Please connect your
            Google Calendar to add this service.
          </p>
        </SortupDialog>
      )}
    </>
  );
};

export default ProductFrom;
